import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { caniasRequestAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CaniasRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('open'); // open, resolved
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium'
  });
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab === 'open') {
        params.status = 'open,in_progress';
      } else {
        params.status = 'resolved,closed';
      }
      const response = await caniasRequestAPI.getAll(params);
      // Filtrele
      const filtered = response.data.filter(r => {
        if (activeTab === 'open') {
          return r.status === 'open' || r.status === 'in_progress';
        }
        return r.status === 'resolved' || r.status === 'closed';
      });
      setRequests(filtered);
    } catch (error) {
      toast.error('Talepler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await caniasRequestAPI.create(formData);
      toast.success('Talep oluşturuldu');
      setShowModal(false);
      setFormData({ title: '', description: '', priority: 'medium' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Talep oluşturulamadı');
    }
  };

  const handleResolve = async () => {
    if (!selectedRequest) return;
    try {
      await caniasRequestAPI.update(selectedRequest.id, {
        status: 'resolved',
        resolution
      });
      toast.success('Talep çözüldü olarak işaretlendi');
      setShowDetailModal(false);
      setResolution('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await caniasRequestAPI.update(requestId, { status: newStatus });
      toast.success('Durum güncellendi');
      fetchRequests();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Düşük' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Orta' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Yüksek' },
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Kritik' }
    };
    const c = config[priority] || config.medium;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Açık' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'İşlemde' },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Çözüldü' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Kapatıldı' }
    };
    const c = config[status] || config.open;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const openDetail = (request) => {
    setSelectedRequest(request);
    setResolution(request.resolution || '');
    setShowDetailModal(true);
  };

  // İç Denetim Şefliği kontrolü (departman bazlı)
  const isIcDenetim = user?.departmentInfo?.name?.toLowerCase().includes('denetim') ||
                      user?.role === 'admin' ||
                      user?.role === 'it';

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-indigo-600 dark:text-indigo-400">IAS</span> Canias Talepleri
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">ERP sistemi destek talepleri</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Yeni Talep
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('open')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'open'
              ? 'bg-indigo-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <ClockIcon className="h-5 w-5 mr-2" />
          Açık Talepler
        </button>
        <button
          onClick={() => setActiveTab('resolved')}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'resolved'
              ? 'bg-green-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          <CheckCircleIcon className="h-5 w-5 mr-2" />
          Çözülenler (IAS)
        </button>
      </div>

      {/* Request List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {activeTab === 'open' ? 'Açık talep bulunmuyor' : 'Çözülmüş talep bulunmuyor'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Başlık</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Talep Eden</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Öncelik</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Durum</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tarih</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">İşlem</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600 dark:text-indigo-400">
                      #{request.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{request.title}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{request.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {request.requester?.fullName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(request.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(request.createdAt).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openDetail(request)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                      >
                        Detay
                      </button>
                      {isIcDenetim && request.status === 'open' && (
                        <button
                          onClick={() => handleStatusChange(request.id, 'in_progress')}
                          className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300"
                        >
                          İşleme Al
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Yeni Canias Talebi</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlık *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Talep başlığı"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={4}
                  placeholder="Sorununuzu detaylı açıklayın"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Öncelik</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="low">Düşük</option>
                  <option value="medium">Orta</option>
                  <option value="high">Yüksek</option>
                  <option value="critical">Kritik</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Talep Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Talep #{selectedRequest.id}
              </h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Başlık</label>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{selectedRequest.title}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Açıklama</label>
                <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">{selectedRequest.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Talep Eden</label>
                  <p className="text-gray-900 dark:text-white">{selectedRequest.requester?.fullName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">E-posta</label>
                  <p className="text-gray-900 dark:text-white">{selectedRequest.requester?.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Öncelik</label>
                  <div className="mt-1">{getPriorityBadge(selectedRequest.priority)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Durum</label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Oluşturma Tarihi</label>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(selectedRequest.createdAt).toLocaleString('tr-TR')}
                  </p>
                </div>
                {selectedRequest.resolvedAt && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Çözüm Tarihi</label>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(selectedRequest.resolvedAt).toLocaleString('tr-TR')}
                    </p>
                  </div>
                )}
              </div>

              {/* Çözüm alanı */}
              {(selectedRequest.status === 'resolved' || selectedRequest.status === 'closed') && selectedRequest.resolution && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Çözüm</label>
                  <p className="text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/30 p-3 rounded-lg border border-green-200 dark:border-green-700">
                    {selectedRequest.resolution}
                  </p>
                </div>
              )}

              {/* İç Denetim için çözüm girişi */}
              {isIcDenetim && (selectedRequest.status === 'open' || selectedRequest.status === 'in_progress') && (
                <div className="border-t dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Çözüm Açıklaması</label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={3}
                    placeholder="Çözümü açıklayın..."
                  />
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => handleStatusChange(selectedRequest.id, 'in_progress')}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                      disabled={selectedRequest.status === 'in_progress'}
                    >
                      İşleme Al
                    </button>
                    <button
                      onClick={handleResolve}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-5 w-5 inline mr-1" />
                      Çözüldü Olarak İşaretle
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaniasRequests;
